/** Rapporto larghezza/altezza target story (9:16). */
export const STORY_ASPECT = 9 / 16;

/** Risoluzione export (standard Meta Stories). */
export const STORY_EXPORT_WIDTH = 1080;
export const STORY_EXPORT_HEIGHT = 1920;

export const STORY_JPEG_QUALITY = 0.96;

/** Altezza/larghezza minima consigliata (≈ 9:16). */
const STORY_MIN_HW_RATIO = 1.5;

/**
 * @returns {'good'|'warn'|'bad'}
 */
export function storyAspectFit(width, height) {
  if (!width || !height) {
    return 'warn';
  }
  const hw = height / width;
  if (hw < 1) {
    return 'bad';
  }
  if (hw < STORY_MIN_HW_RATIO) {
    return 'warn';
  }
  return 'good';
}

function loadImage(src) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error('image_load_failed'));
    img.src = src;
  });
}

export function readImageFileDimensions(file) {
  const url = URL.createObjectURL(file);
  return loadImage(url).then((img) => {
    URL.revokeObjectURL(url);
    return { width: img.naturalWidth, height: img.naturalHeight };
  });
}

export function readImageUrlDimensions(url) {
  return loadImage(url).then((img) => ({
    width: img.naturalWidth,
    height: img.naturalHeight,
  }));
}

/**
 * Area di ritaglio 9:16 all’interno dell’immagine sorgente.
 * @returns {{ cropW: number, cropH: number, sx: number, sy: number, minSx: number, maxSx: number, minSy: number, maxSy: number, panAxis: 'x'|'y'|'none' }}
 */
export function computeStoryCropBox(width, height) {
  const srcRatio = width / height;
  if (srcRatio > STORY_ASPECT) {
    const cropH = height;
    const cropW = height * STORY_ASPECT;
    const maxSx = width - cropW;
    return {
      cropW,
      cropH,
      sx: maxSx / 2,
      sy: 0,
      minSx: 0,
      maxSx,
      minSy: 0,
      maxSy: 0,
      panAxis: maxSx > 0 ? 'x' : 'none',
    };
  }
  const cropW = width;
  const cropH = width / STORY_ASPECT;
  const maxSy = height - cropH;
  return {
    cropW,
    cropH,
    sx: 0,
    sy: maxSy / 2,
    minSx: 0,
    maxSx: 0,
    minSy: 0,
    maxSy,
    panAxis: maxSy > 0 ? 'y' : 'none',
  };
}

function canvasToJpegBlob(canvas, quality = STORY_JPEG_QUALITY) {
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (blob) {
          resolve(blob);
        } else {
          reject(new Error('blob_failed'));
        }
      },
      'image/jpeg',
      quality
    );
  });
}

/**
 * Export JPEG 1080×1920 dal rettangolo sorgente scelto.
 * @param {string} imageUrl
 * @param {{ sx: number, sy: number, cropW: number, cropH: number }} crop
 */
export async function exportStoryCropBlob(imageUrl, crop) {
  const img = await loadImage(imageUrl);
  const { sx, sy, cropW, cropH } = crop;

  const canvas = document.createElement('canvas');
  canvas.width = STORY_EXPORT_WIDTH;
  canvas.height = STORY_EXPORT_HEIGHT;
  const ctx = canvas.getContext('2d', { alpha: false });
  if (!ctx) {
    throw new Error('canvas_unavailable');
  }
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = 'high';
  ctx.drawImage(img, sx, sy, cropW, cropH, 0, 0, STORY_EXPORT_WIDTH, STORY_EXPORT_HEIGHT);

  return canvasToJpegBlob(canvas);
}

/**
 * Ritaglio centrato 9:16 (senza editor).
 * @param {File|string} fileOrUrl
 * @returns {Promise<Blob>}
 */
export async function cropImageToStory916(fileOrUrl) {
  const src =
    fileOrUrl instanceof File ? URL.createObjectURL(fileOrUrl) : String(fileOrUrl);
  const shouldRevoke = fileOrUrl instanceof File;

  try {
    const img = await loadImage(src);
    const box = computeStoryCropBox(img.naturalWidth, img.naturalHeight);
    return exportStoryCropBlob(src, {
      sx: box.sx,
      sy: box.sy,
      cropW: box.cropW,
      cropH: box.cropH,
    });
  } finally {
    if (shouldRevoke) {
      URL.revokeObjectURL(src);
    }
  }
}
